import { useEffect, useRef } from 'react'
import * as firebaseui from 'firebaseui'

import { FirebaseAuthWidgetProps } from './types'

/**
 * Thin React wrapper around `firebaseui.auth.AuthUI`. We do NOT use
 * `react-firebaseui` because its peer-dep range tops out at React 17 and
 * fights with React 19 on install — this wrapper is the same pattern in
 * 20 lines and avoids the dependency entirely.
 *
 * The widget is mounted into a container div via `start()` on mount and
 * cleaned up via `reset()` on unmount. AuthUI is a singleton — subsequent
 * mounts grab the existing instance via `getInstance()`.
 *
 * @param {FirebaseAuthWidgetProps} props
 * @returns {JSX.Element}
 */
const FirebaseAuthWidget = ({
  firebaseAuth,
  uiConfig,
}: FirebaseAuthWidgetProps): JSX.Element => {
  const containerReference = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const containerNode = containerReference.current

    if (containerNode === null) {
      return
    }

    const existingInstance = firebaseui.auth.AuthUI.getInstance()
    const authUi = existingInstance ?? new firebaseui.auth.AuthUI(firebaseAuth)

    authUi.start(containerNode, uiConfig)

    return () => {
      authUi.reset()
    }
  }, [firebaseAuth, uiConfig])

  return <div ref={containerReference} />
}

export default FirebaseAuthWidget
